import { useState, useEffect, FocusEvent } from 'react'

import { InputProps } from './Input'

/**
 * When you have a component that has multiple `<input>`s, but you want
 * it to act like it's a single input, use this hook. It bascially
 * allows focus to move between internal inputs without calling the
 * `onBlur`|`onFocus` handlers.
 */
export const useInputFocus = ({
  onFocus,
  onBlur,
}: Pick<InputProps, 'onFocus' | 'onBlur'>) => {
  const [focused, setFocused] = useState<FocusEvent<HTMLInputElement> | null>(
    null
  )
  const [blurred, setBlurred] = useState<FocusEvent<HTMLInputElement> | null>(
    null
  )

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    e.persist()
    if (onFocus) onFocus(e)
    setFocused(e)
  }
  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    e.persist()
    setBlurred(e)
  }

  // Debounce the blur event
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!focused || !blurred) return
      if (!focused.target || !blurred.target) return
      if (focused.target === blurred.target && onBlur) {
        // User has focused away from the input
        if (onBlur) onBlur(blurred)
        setFocused(null)
        setBlurred(null)
      }
    }, 150)
    return () => {
      clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blurred, focused])

  return {
    handleFocus,
    handleBlur,
  }
}
